import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import TeamsTable from './TeamsTable';
import TeamsTableMobile from './TeamsTableMobile';
import TeamModal from './TeamModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';

import {
  fetchTeams,
  updateTeam,
  deleteTeam,
  createTeam,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './teams.scss';

const TeamsTableContainer = (props) => {
  const {
    teams = [],
    distributors,
    createTeam,
    updateTeam,
    deleteTeam,
    fetchTeams,
    withButton,
    toggleSidebar,
    countries,
    users,
    procedures,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const onEditClick = (team) => {
    setSelectedTeam(team);
    toggleUpdateModal(true);
  };

  const onTeamCreate = async (formObj) => {
    try {
      setLoading(true);

      await createTeam(formObj);
      await fetchTeams();
      setSuccessMessage('New team successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onTeamUpdate = async (formObj) => {
    if (selectedTeam) {
      try {
        setLoading(true);

        await updateTeam(selectedTeam?.id, formObj);
        await fetchTeams();
        setSuccessMessage('Team successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteTeam = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteTeam(selectedId);
        await fetchTeams();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const filteredTeams = useMemo(() => {
    let filtered = teams;

    if (search) {
      const q = search.toLowerCase();
      filtered = teams.filter((p) => p?.name?.toLowerCase()?.includes(q));
    }

    return filtered;
  }, [teams, search]);

  const list = useMemo(() => {
    let result = filteredTeams;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredTeams, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <IconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Teams
                  <Box className="badge">{teams.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    View and manage your teams
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <Button
              text='Add Team'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {teams.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TeamsTable
                teams={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredTeams?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                procedures={procedures}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <TeamsTableMobile teams={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Teams</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteTeam}
        title="Delete Team"
        text="Are you sure you want to delete this team?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <TeamModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onTeamCreate}
        countriesList={countries}
        usersList={users}
        loading={loading}
        proceduresList={procedures}
      />

      <TeamModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedTeam(undefined);
        }}
        onSubmit={onTeamUpdate}
        countriesList={countries}
        usersList={users}
        mode='update'
        initialValues={selectedTeam}
        loading={loading}
        proceduresList={procedures}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteTeam}
        title="Delete Team?"
        text="Are you sure you want to delete this team? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

TeamsTableContainer.propTypes = {
  teams: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchTeams: () => dispatch(fetchTeams()),
    createTeam: (teamData) => dispatch(createTeam(teamData)),
    updateTeam: (id, teamData) => dispatch(updateTeam(id, teamData)),
    deleteTeam: (id) => dispatch(deleteTeam(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamsTableContainer);
